import { defineStore } from "pinia";
import { useRawCookie } from "@/composables/useRawCookie";
import { localStorageSet } from "@/utils/internet/local-storage";
import { login } from "~/services/auth";
import { useSetRefreshTokenExpiration } from "~/composables/useSetRefreshTokenExpiration";
import { useSetTokenExpiration } from "~/composables/useSetTokenExpiration";

export interface Account {
  shortId: string;
  nickName: string;
  device: string;
  ip: string;
  createdAt: string;
  nid: string;
  source: string;
  languageId: string;
  email: string;
  fullName: string;
  updatedAt: string;
  confirmedMfa: string;
  id: string;
  phone: string;
}

interface SignInRequest {
  nid: string;
  password: string;
}

export interface SignInResponse {
  token: string;
  refresh_token: string;
  exp: number;
  account_id: string;
  account: Account;
}

const THIRTY_DAYS = 60 * 60 * 24 * 30;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null as SignInResponse | null,
    loading: false,
    authenticated: false,
    hurstDomain: useRuntimeConfig()?.public?.hurstDomain,
  }),
  getters: {
    isAuthenticated: (state) => state.authenticated,
    isLoading: (state) => state.loading,
    cookiesOptions: (state) => {
      return {
        path: "/",
        maxAge: THIRTY_DAYS,
        domain: state.hurstDomain as string | undefined,
      };
    },
  },
  actions: {
    async signIn({ nid, password }: SignInRequest) {
      const setRefreshTokenExpiration = useSetRefreshTokenExpiration();
      const setTokenExpiration = useSetTokenExpiration();
      this.loading = true;

      try {
        const httpResponse = await login({ nid, password });
        const data = httpResponse;

        localStorageSet("HCP-AUTH", {
          isAuth: true,
          exp: data.expiresIn,
          refresh_token: data.refreshToken,
          token: data.accessToken,
          account_id: data.account.id,
          account: data.account,
        });

        this.authenticated = true;

        const accountCookie = useCookie("account", this.cookiesOptions);
        const nidCookie = useRawCookie("nid", this.cookiesOptions);

        const tokenCookie = useCookie("token", this.cookiesOptions);
        const refreshTokenCookie = useCookie(
          "refresh_token",
          this.cookiesOptions,
        );

        tokenCookie.value = data.accessToken;
        refreshTokenCookie.value = data.refreshToken;
        accountCookie.value = data.account.id;
        nidCookie.value = data.account.nid;

        setRefreshTokenExpiration(data.refreshTokenExpiresIn);
        setTokenExpiration(data.accessTokenExpiresIn ?? data?.expiresIn);

        return data;
      } catch (error) {
        return null;
      } finally {
        this.loading = false;
      }
    },
    signOut() {
      this.setAuthenticated(false);

      const tokenCookie = useCookie("token", this.cookiesOptions);
      tokenCookie.value = null;

      const refreshTokenCookie = useCookie(
        "refresh_token",
        this.cookiesOptions,
      );
      refreshTokenCookie.value = null;

      const accountCookie = useCookie("account", this.cookiesOptions);
      accountCookie.value = null;

      const nidCookie = useRawCookie("nid", this.cookiesOptions);
      nidCookie.value = null;

      const refreshExpCookie = useCookie("refresh_exp", this.cookiesOptions);
      refreshExpCookie.value = null;

      const tokenExpCookie = useCookie("token_exp", this.cookiesOptions);
      tokenExpCookie.value = null;
    },
    setAuthenticated(IsAuthenticated: boolean) {
      this.authenticated = IsAuthenticated;
    },
  },
});
